import React, { Component } from 'react';
import Form from 'react-bootstrap/Form';

import Select from 'components/controls/select';
import CellWorkspaceView, { WORKSPACE_MODE_LABELS } from './cellWorkspaceView';
import { isStructureEditable } from './cellRules';
import { workspaceSelection } from '../../domain/cellWorkflow';
import { upsertCell } from '../../domain/model';
import { WORKSPACE_MODES } from '../../domain/schema';

/**
 * CellWorkspaceSection — where a cell's agent works, choosable or frozen, and the rule for which.
 *
 * The third of the three sections that describe a cell, and the last to be shared: the mission
 * dock had it and the cell's own screen had no Workspace section at all — not read-only, absent.
 * A person on that screen could not see where the agent would run, let alone say.
 *
 * The rule is the sections' rule, owned here rather than by either surface: editable while the
 * cell is a draft AND the revision and session allow it, frozen after.
 *
 * A workspace is an ACTUAL place, not a template. What is written is a COPY of the palette entry's
 * `{ type, work_folder }`, never a reference by name — the palette moves on and the cell does not,
 * and a run has to keep working in the place it was given. Which is also why the current selection
 * is reverse-matched by `work_folder`: the copy on the cell does not retain the name it came from.
 *
 * The mode and the workspace travel TOGETHER on every write, for the reason the cell-workflow pair
 * does: a mode change without its resolved workspace leaves the two inconsistent server-side.
 * `specify_workspace` is the only mode that resolves one HERE; both inherit modes are resolved at
 * dispatch, from the mission or the previous cell, so picking one writes the mode with no place.
 *
 * Props:
 *   cell, missionId, client   — what to write and where
 *   templates                 — store.templates; `workspaces` is the palette to choose from
 *   onApplyLocal              — (updater) => void; reflect the write without a reload
 *   canEdit                   — false on a committed revision, a closed session, or no write access
 */
const NONE_OPTION = { value: '', label: '— none —' };

class CellWorkspaceSection extends Component {
  _palette() {
    return (this.props.templates && this.props.templates.workspaces) || [];
  }

  async _save(mode, workspace) {
    const { cell, missionId, client, onApplyLocal } = this.props;
    if (!cell || !client) return;
    const changed = { workspace_mode: mode, workspace };
    try {
      await client.setCellProps(missionId, cell.element_id, changed);
      if (onApplyLocal) {
        onApplyLocal(s => upsertCell(s, missionId, {
          ...cell, props: { ...(cell.props || {}), ...changed },
        }));
      }
    } catch (_) {
      // Ignore; a later reload will reflect the server state.
    }
  }

  async _saveMode(mode) {
    const { cell } = this.props;
    const next = mode || '';
    if (next === ((cell.props || {}).workspace_mode || '')) return;
    // No place with the mode: `specify_workspace` resolves one through the picker below, and both
    // inherit modes are resolved at dispatch.
    await this._save(next || null, null);
  }

  async _saveWorkspace(alias) {
    const { cell } = this.props;
    const palette = this._palette();
    const current = (cell.props || {}).workspace || null;
    const currentEntry = current
      ? palette.find(entry => entry.work_folder === current.work_folder)
      : null;
    const selected = alias || '';
    if (selected === ((currentEntry && currentEntry.alias) || '')) return;
    const entry = selected ? palette.find(item => item.alias === selected) : null;
    await this._save((cell.props || {}).workspace_mode || 'specify_workspace',
                     entry ? { type: entry.type, work_folder: entry.work_folder } : null);
  }

  render() {
    const { cell, canEdit } = this.props;
    if (!isStructureEditable(cell) || canEdit === false) {
      return <CellWorkspaceView cell={cell} />;
    }

    const props = cell.props || {};
    const palette = this._palette();
    const modeOptions = WORKSPACE_MODES.map(
      value => ({ value, label: WORKSPACE_MODE_LABELS[value] || value }));
    const options = [
      NONE_OPTION,
      ...palette.map(entry => entry.alias).filter(Boolean)
        .map(alias => ({ value: alias, label: alias })),
    ];
    // `{alias}` when the palette still has this workspace, `{folder}` when it has drifted from it,
    // null when the cell genuinely has none — see workspaceSelection.
    const selected = workspaceSelection(props.workspace || null, palette);
    if (selected && selected.folder) {
      // An option that exists only for this cell, so the picker names what the run will use instead
      // of claiming nothing is set. Choosing a palette entry stays one click away.
      options.push({ value: selected.folder, label: selected.folder });
    }

    return (
      <>
        <Form.Group>
          <Select
            options={modeOptions}
            value={modeOptions.find(option => option.value === (props.workspace_mode || null)) || null}
            onChange={option => this._saveMode(option ? option.value : '')}
            menuPortalTarget={document.body}
            placeholder="Choose a mode…"
          />
          <Form.Text muted>How the workspace is chosen.</Form.Text>
        </Form.Group>
        {props.workspace_mode === 'specify_workspace' && (
          <Form.Group>
            <Select
              options={options}
              value={(selected && options.find(
                option => option.value === (selected.alias || selected.folder))) || NONE_OPTION}
              onChange={option => this._saveWorkspace(option ? option.value : '')}
              menuPortalTarget={document.body}
              placeholder="— none —"
            />
            <Form.Text muted>Where the agent works.</Form.Text>
          </Form.Group>
        )}
      </>
    );
  }
}

export default CellWorkspaceSection;
